import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";

interface Photo {
  id: number;
  preview_url: string;
  download_url: string;
  alt: string;
  photographer: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultQuery: string;
  onSelect: (photo: Photo) => void;
}

const ImageGridPicker = ({ open, onOpenChange, defaultQuery, onSelect }: Props) => {
  const [query, setQuery] = useState(defaultQuery);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);

  const search = async (q: string) => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("search-course-images", {
        body: { query: q, per_page: 6 },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      setPhotos(((data as any)?.photos ?? []).slice(0, 6));
    } catch (e: any) {
      toast.error(e?.message || "Recherche échouée");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setQuery(defaultQuery); setPhotos([]); search(defaultQuery); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultQuery]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Choisir une image (6 propositions)</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Mot-clé de recherche…"
            onKeyDown={(e) => { if (e.key === "Enter") search(query); }} />
          <Button onClick={() => search(query)} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Régénérer 6
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-auto">
          {loading && Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-video bg-muted animate-pulse rounded-lg" />
          ))}
          {!loading && photos.map((p) => (
            <button key={p.id} onClick={() => { onSelect(p); onOpenChange(false); }}
              className="group relative aspect-video overflow-hidden rounded-lg border border-border hover:border-primary transition">
              <img src={p.preview_url} alt={p.alt} className="w-full h-full object-cover group-hover:scale-105 transition" loading="lazy" />
              <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/40 flex items-center justify-center transition">
                <Check className="h-8 w-8 text-white opacity-0 group-hover:opacity-100" />
              </div>
              <span className="absolute bottom-1 left-1 text-[10px] text-white bg-black/50 px-1.5 rounded">{p.photographer}</span>
            </button>
          ))}
          {!loading && photos.length === 0 && (
            <p className="col-span-full text-center text-sm text-muted-foreground py-8">Aucune image trouvée.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageGridPicker;